// calendar-2 (Carla's round-5 decision, 2026-10-12): in the task detail, the lasting mark of a
// change a calendar did not take: "Outlook did not take this change on <date>: <reason>." with
// Retry (POST /api/tasks/:id/calendar-sync/retry, the same people as may edit the task).
// States: none (nothing shown), error, retrying, retry failed. The line goes only when the marks
// the server READ BACK after the retry say so.
import React, { useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/formatting';
import { useCalendarSyncErrors, replaceCalendarSyncErrorsForTask } from '../../utils/calendarSyncErrors';
import { Banner, Button } from '../../ui';
import './calendarSyncNotice.css';

// An occurrence of a repeating task is drawn from its series: the mark is the series'.
const seriesIdOf = (task) => (task && task.isRecurringInstance && task.parentTaskId !== undefined && task.parentTaskId !== null ? task.parentTaskId : task?.id);
const sentence = (text) => {
  const t = String(text || 'no reason was given').trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
};

export default function CalendarSyncNotice({ task }) {
  const taskId = seriesIdOf(task);
  const marks = useCalendarSyncErrors(taskId);
  const [retrying, setRetrying] = useState(false);
  const [retryProblem, setRetryProblem] = useState(null);

  if (!marks.length) return null;

  const retry = async () => {
    setRetrying(true);
    setRetryProblem(null);
    try {
      const res = await api(`/api/tasks/${taskId}/calendar-sync/retry`, { method: 'POST' });
      const data = await res.json();
      const left = Array.isArray(data?.calendarSyncErrors) ? data.calendarSyncErrors : null;
      if (!left) {
        setRetryProblem('Retry did not work: the answer did not say whether the calendar took it.');
        return;
      }
      replaceCalendarSyncErrorsForTask(taskId, left);
      if (left.length) {
        const refused = (Array.isArray(data.calendarSync) ? data.calendarSync : []).filter((c) => c && c.ok === false);
        setRetryProblem(`Retry did not work${refused.length ? `: ${refused.map((c) => `${c.label} said ${sentence(c.error)}`).join(' ')}` : '.'}`);
      }
    } catch (err) {
      setRetryProblem(`Retry did not work: ${sentence(messageFromError(err, 'the calendars could not be asked again.'))}`);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="calendar-sync-notice" data-testid="calendar-sync-notice">
      {marks.map((m) => (
        <Banner
          key={m.provider}
          tone="problem"
          action={(
            <Button variant="secondary" size="sm" onClick={retry} disabled={retrying} data-testid={`calendar-sync-retry-${m.provider}`}>
              {retrying ? 'Retrying…' : 'Retry'}
            </Button>
          )}
        >
          {`${m.label} did not take this change${m.at ? ` on ${formatDateTime(m.at)}` : ''}: ${sentence(m.error)}`}
        </Banner>
      ))}
      {retryProblem && <p className="calendar-sync-notice__problem" role="alert">{retryProblem}</p>}
    </div>
  );
}
